import { useEffect, useRef, useState, useCallback } from "react";
import { WebPubSubClient } from "@azure/web-pubsub-client";

export interface WebPubSubMessage {
  type: string;
  [key: string]: any;
}

// Define the event payload type for WebPubSub events
interface WebPubSubEvent {
  message?: { data: WebPubSubMessage };
  data?: WebPubSubMessage;
  [key: string]: any;
}

export function useWebPubSub({
  userId,
  onMessage,
  enabled = true,
  negotiationUrl = "/api/negotiate",
}: {
  userId: string;
  onMessage?: (msg: WebPubSubMessage) => void;
  enabled?: boolean;
  negotiationUrl?: string;
}) {
  const [connected, setConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebPubSubMessage | null>(null);
  const clientRef = useRef<WebPubSubClient | null>(null);

  useEffect(() => {
    console.log("[WebPubSub] useEffect fired", {
      userId,
      enabled,
      negotiationUrl,
    });
    if (!enabled || !userId || !negotiationUrl) {
      console.log("[WebPubSub] Skipping connect: enabled, userId, or negotiationUrl missing", {
        enabled,
        userId,
        negotiationUrl,
      });
      return;
    }

    // Additional safety check - if negotiationUrl is empty string, don't even attempt connection
    if (negotiationUrl.trim() === "") {
      console.log("[WebPubSub] Skipping connect: negotiationUrl is empty string");
      return;
    }
    let isMounted = true;
    let client: WebPubSubClient | null = null;

    async function connect() {
      try {
        console.log("[WebPubSub] Starting negotiation for userId:", userId);
        // Call negotiation endpoint
        const res = await fetch(`${negotiationUrl}?userId=${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error("Negotiation failed");
        const { url, accessToken: token } = await res.json();
        if (!url) throw new Error("No WebSocket URL returned");
        console.log("[WebPubSub] Negotiation URL received:", url);
        if (token) {
          localStorage.setItem("webPubSubToken", token);
          console.log("[WebPubSub] WebPubSub token set:", token);
        }

        // Connect to Azure Web PubSub
        client = new WebPubSubClient(url);
        clientRef.current = client;

        client.on("connected", () => {
          if (isMounted) setConnected(true);
          // TODO: uncomment when adding functionality for messaging groups
          /* client?.joinGroup('default').then(() => {
            console.log('Joined group: default');
          }).catch((err: any) => {
            console.error('Failed to join group:', err);
          }); */
        });
        client.on("disconnected", () => {
          if (isMounted) setConnected(false);
          console.log("[WebPubSub] Disconnected");
        });
        (client as any).on("server-message", (event: WebPubSubEvent) => {
          console.log("WebPubSub server-message event received:", event);
          if (isMounted) {
            const data: WebPubSubMessage | null = event.message?.data ?? event.data ?? null;
            setLastMessage(data);
            if (onMessage && data) onMessage(data);
          }
        });
        (client as any).on("group-message", (event: WebPubSubEvent) => {
          console.log("WebPubSub group-message event received:", event);
          if (isMounted) {
            const data: WebPubSubMessage | null = event.message?.data ?? event.data ?? null;
            setLastMessage(data);
            if (onMessage && data) onMessage(data);
          }
        });

        await client.start();
      } catch (err) {
        if (isMounted) setConnected(false);
        console.error("[WebPubSub] Error during connect:", err);
      }
    }

    connect();
    return () => {
      isMounted = false;
      // Stop both the clientRef and the local client if they exist and are not the same
      if (clientRef.current) {
        clientRef.current.stop();
        clientRef.current = null;
        console.log("[WebPubSub] Client stopped and cleaned up (clientRef.current)");
      }
      if (client && client !== clientRef.current) {
        client.stop();
        console.log("[WebPubSub] Client stopped and cleaned up (local client)");
      }
    };
  }, [userId, enabled, negotiationUrl]);

  // Optionally, expose a send method
  const sendMessage = useCallback(
    (data: any) => {
      if (clientRef.current && connected) {
        clientRef.current.sendToGroup("default", data, "json"); // specify dataType
      }
    },
    [connected]
  );

  return { connected, lastMessage, sendMessage };
}
